<template>
  <section class="duallo-admin-table">
    <header class="duallo-table-header">
      <div>
        <div class="duallo-eyebrow">Organization</div>
        <h3 class="duallo-table-title">Sites</h3>
      </div>
      <DisplayOverlayWindowButton v-if="canCreate" ref="newSiteWindow" buttonText="Add Site" iconName="mdi-plus"
        windowTitle="Add Site">
        <div class="duallo-form site-form">
          <header class="duallo-form-header">
            <div class="duallo-eyebrow">New</div>
            <h2 class="duallo-form-title">Site</h2>
            <p class="duallo-form-subtitle">Add a site (e.g. "Pasadena Oil Terminal Site") for this organization.</p>
          </header>

          <div v-if="submitError" class="duallo-inline-error">
            <v-icon size="18" color="#ff8a80">mdi-alert-circle-outline</v-icon>
            <span>{{ submitError }}</span>
          </div>

          <v-form ref="createForm" @submit.prevent="handleCreate">
            <div class="duallo-form-fields">
              <v-text-field v-model="createTitle" label="Title" variant="outlined" density="comfortable"
                hide-details="auto" class="duallo-field" :rules="[v => !!(v && v.trim()) || 'Title is required']" />
              <v-textarea v-model="createDescription" label="Description (optional)" variant="outlined"
                density="comfortable" hide-details="auto" rows="2" auto-grow class="duallo-field" />
            </div>

            <div class="duallo-form-actions form-actions">
              <button type="submit" class="duallo-btn duallo-btn--primary" :disabled="isSubmitting" @click="handleCreate">
                <v-progress-circular v-if="isSubmitting" indeterminate size="16" width="2" color="#071514" />
                <span v-else>Add Site</span>
              </button>
            </div>
          </v-form>
        </div>
      </DisplayOverlayWindowButton>
    </header>

    <div class="duallo-table-card">
      <div v-if="isLoading" class="duallo-state-block">
        <div v-for="n in 3" :key="n" class="duallo-row-skeleton" />
      </div>

      <div v-else-if="loadError" class="duallo-state-block empty-state">
        <v-icon size="40" color="#ff8a80">mdi-alert-circle-outline</v-icon>
        <p class="empty-title">Couldn't load sites</p>
        <p class="empty-subtitle">Something went wrong fetching sites. Please try again.</p>
      </div>

      <div v-else-if="sites.length === 0" class="duallo-state-block empty-state">
        <v-icon size="40" color="#4a5a60">mdi-map-marker-outline</v-icon>
        <p class="empty-title">No sites yet</p>
        <p class="empty-subtitle">Add a site to get started.</p>
      </div>

      <template v-else>
        <div v-if="!isCompact" class="duallo-table-wrap">
          <table class="duallo-data-table">
            <thead>
              <tr>
                <th class="col-name">Site</th>
                <th class="col-desc">Description</th>
                <th class="col-type">Created</th>
                <th class="col-actions"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="site in sites" :key="site.id">
                <td class="cell-name">
                  <div class="cell-name-main">{{ site.title }}</div>
                </td>
                <td>
                  <span v-if="site.description" class="cell-desc">{{ site.description }}</span>
                  <span v-else class="muted">—</span>
                </td>
                <td>
                  <span class="muted-date">{{ formatDate(site.createdAt) }}</span>
                </td>
                <td class="cell-actions">
                  <button v-if="canUpdate" type="button" class="duallo-btn duallo-btn--ghost" @click="openEdit(site)">
                    <v-icon size="16">mdi-pencil-outline</v-icon>
                    <span>Edit</span>
                  </button>
                  <button v-if="canDelete" type="button" class="duallo-btn duallo-btn--ghost is-danger" @click="confirmDelete(site)">
                    <v-icon size="16">mdi-trash-can-outline</v-icon>
                    <span>Delete</span>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else class="duallo-card-list">
          <article v-for="site in sites" :key="site.id" class="duallo-list-card card">
            <div class="card-top">
              <div>
                <div class="card-name">{{ site.title }}</div>
                <div class="cell-name-sub">{{ formatDate(site.createdAt) }}</div>
              </div>
            </div>
            <p v-if="site.description" class="card-desc">{{ site.description }}</p>
            <div class="card-actions">
              <button v-if="canUpdate" type="button" class="duallo-btn duallo-btn--ghost" @click="openEdit(site)">
                <v-icon size="16">mdi-pencil-outline</v-icon>
                <span>Edit</span>
              </button>
              <button v-if="canDelete" type="button" class="duallo-btn duallo-btn--ghost is-danger" @click="confirmDelete(site)">
                <v-icon size="16">mdi-trash-can-outline</v-icon>
                <span>Delete</span>
              </button>
            </div>
          </article>
        </div>
      </template>
    </div>

    <!-- Edit dialog -->
    <v-dialog v-model="editOpen" max-width="460" persistent>
      <div class="duallo-modal">
        <header class="duallo-modal-header">
          <div class="duallo-eyebrow">Edit</div>
          <h2 class="duallo-form-title">Site</h2>
        </header>

        <div v-if="editError" class="duallo-inline-error">
          <v-icon size="18" color="#ff8a80">mdi-alert-circle-outline</v-icon>
          <span>{{ editError }}</span>
        </div>

        <v-form ref="editForm" @submit.prevent="handleEdit">
          <div class="duallo-form-fields">
            <v-text-field v-model="editTitle" label="Title" variant="outlined" density="comfortable"
              hide-details="auto" class="duallo-field" :rules="[v => !!(v && v.trim()) || 'Title is required']" />
            <v-textarea v-model="editDescription" label="Description (optional)" variant="outlined"
              density="comfortable" hide-details="auto" rows="2" auto-grow class="duallo-field" />
          </div>
          <div class="duallo-modal-actions modal-actions">
            <button type="button" class="duallo-btn duallo-btn--text" :disabled="isEditing" @click="editOpen = false">Cancel</button>
            <button type="submit" class="duallo-btn duallo-btn--primary" :disabled="isEditing" @click="handleEdit">
              <v-progress-circular v-if="isEditing" indeterminate size="16" width="2" color="#071514" />
              <span v-else>Save</span>
            </button>
          </div>
        </v-form>
      </div>
    </v-dialog>

    <!-- Delete confirm dialog -->
    <v-dialog v-model="deleteOpen" max-width="420" persistent>
      <div class="duallo-modal">
        <header class="duallo-modal-header">
          <div>
            <div class="duallo-eyebrow">Delete</div>
            <h2 class="duallo-form-title">{{ pendingDelete?.title }}</h2>
            <p class="duallo-form-subtitle">This removes the site from the organization.</p>
          </div>
        </header>
        <div v-if="deleteError" class="duallo-inline-error">
          <v-icon size="18" color="#ff8a80">mdi-alert-circle-outline</v-icon>
          <span>{{ deleteError }}</span>
        </div>
        <div class="duallo-modal-actions modal-actions">
          <button type="button" class="duallo-btn duallo-btn--text" :disabled="isDeleting" @click="deleteOpen = false">Cancel</button>
          <button type="button" class="duallo-btn duallo-btn--danger" :disabled="isDeleting" @click="handleDelete">
            <v-progress-circular v-if="isDeleting" indeterminate size="16" width="2" color="#fff" />
            <span v-else>Delete</span>
          </button>
        </div>
      </div>
    </v-dialog>
  </section>
</template>

<script setup>
import { defineOptions, onMounted, ref, computed } from 'vue';
import { useDisplay } from 'vuetify';
import DisplayOverlayWindowButton from '../buttons/DisplayOverlayWindowButton.vue';
import SiteService from '@/services/SiteService';
import { useOrganizationStore } from '@/store/organizationStore';
import { Permissions } from '@/constants/permissions';
import { usePermissions } from '@/composable/usePermissions';

defineOptions({ name: 'SiteManagementTable' });

const { smAndDown } = useDisplay();
const isCompact = computed(() => smAndDown.value);

const { can } = usePermissions();
const canCreate = computed(() => can(Permissions.SiteCreate));
const canUpdate = computed(() => can(Permissions.SiteUpdate));
const canDelete = computed(() => can(Permissions.SiteDelete));

const organizationStore = useOrganizationStore();
const organizationId = computed(() => organizationStore.organizationId);

const sites = ref([]);
const isLoading = ref(true);
const loadError = ref(false);

// --- Create form ---
const newSiteWindow = ref(null);
const createForm = ref(null);
const createTitle = ref('');
const createDescription = ref('');
const isSubmitting = ref(false);
const submitError = ref('');

// --- Edit dialog ---
const editForm = ref(null);
const editOpen = ref(false);
const editing = ref(null);
const editTitle = ref('');
const editDescription = ref('');
const isEditing = ref(false);
const editError = ref('');

// --- Delete dialog ---
const deleteOpen = ref(false);
const pendingDelete = ref(null);
const isDeleting = ref(false);
const deleteError = ref('');

onMounted(loadSites);

async function loadSites() {
  isLoading.value = true;
  loadError.value = false;
  try {
    sites.value = await SiteService.getSitesByOrganizationId(organizationId.value);
  } catch {
    loadError.value = true;
  } finally {
    isLoading.value = false;
  }
}

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const handleCreate = async () => {
  submitError.value = '';
  const validation = await createForm.value?.validate?.();
  if (validation && validation.valid === false) return;

  isSubmitting.value = true;
  try {
    await SiteService.createSite({
      organizationId: organizationId.value,
      title: createTitle.value.trim(),
      description: createDescription.value?.trim() || null,
    });
    await loadSites();
    createTitle.value = '';
    createDescription.value = '';
    createForm.value?.reset();
    newSiteWindow.value?.closeWindow();
  } catch (e) {
    submitError.value = e?.message || 'Could not add the site. Please try again.';
  } finally {
    isSubmitting.value = false;
  }
};

const openEdit = (site) => {
  editing.value = site;
  editTitle.value = site.title ?? '';
  editDescription.value = site.description ?? '';
  editError.value = '';
  editOpen.value = true;
};

const handleEdit = async () => {
  if (!editing.value) return;
  editError.value = '';
  const validation = await editForm.value?.validate?.();
  if (validation && validation.valid === false) return;

  isEditing.value = true;
  try {
    const updated = await SiteService.updateSite(organizationId.value, editing.value.id, {
      title: editTitle.value.trim(),
      description: editDescription.value?.trim() || null,
    });
    if (updated) {
      const idx = sites.value.findIndex(s => s.id === editing.value.id);
      if (idx !== -1) sites.value[idx] = updated;
    }
    editOpen.value = false;
    editing.value = null;
  } catch (e) {
    editError.value = e?.message || 'Could not update the site. Please try again.';
  } finally {
    isEditing.value = false;
  }
};

const confirmDelete = (site) => {
  pendingDelete.value = site;
  deleteError.value = '';
  deleteOpen.value = true;
};

const handleDelete = async () => {
  if (!pendingDelete.value) return;
  deleteError.value = '';
  isDeleting.value = true;
  try {
    await SiteService.deleteSite(organizationId.value, pendingDelete.value.id);
    sites.value = sites.value.filter(s => s.id !== pendingDelete.value.id);
    deleteOpen.value = false;
    pendingDelete.value = null;
  } catch (e) {
    deleteError.value = e?.message || 'Could not delete the site. Please try again.';
  } finally {
    isDeleting.value = false;
  }
};
</script>

<style scoped>
/* ----- Table-specific column widths ----- */
.col-name { min-width: 200px; }
.col-desc { min-width: 240px; }
.col-type { width: 150px; }
.col-actions { width: 180px; }

/* Row cell padding differs from the shared default (10px vs 12px vertical) */
.duallo-data-table td { padding: 10px 16px; }

.cell-name-main { color: #fff; font-weight: 600; }
.cell-name-sub {
  font-size: 0.78rem;
  color: #8aa0aa;
}

.muted { color: #5e6f76; }
.muted-date { color: #8aa0aa; font-size: 0.85rem; }
.cell-desc { color: #cfd8dc; font-size: 0.88rem; max-width: 420px; display: inline-block; }

.cell-actions { display: flex; gap: 8px; align-items: center; }

/* ----- Empty / error state ----- */
.empty-state {
  align-items: center;
  text-align: center;
  padding: 40px 16px;
  color: #8aa0aa;
}

.empty-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: #cfd8dc;
  margin: 12px 0 4px;
}

.empty-subtitle { font-size: 0.88rem; margin: 0; }

/* ----- Mobile cards ----- */
.card { gap: 10px; }
.card-top { display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; }
.card-name { font-size: 1rem; font-weight: 700; color: #fff; }
.card-desc { color: #b0bec5; font-size: 0.88rem; margin: 0; line-height: 1.45; }
.card-actions { display: flex; gap: 8px; }

/* ----- Form / dialog overrides (differ from shared defaults) ----- */
.site-form { min-width: min(440px, 80vw); }
.form-actions { margin-top: 18px; }
.modal-actions { margin-top: 18px; }
</style>
